import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  Chip,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import CompleteReviewDialog from './components/CompleteReviewDialog';

const PolicyReviewDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);

  // 模拟保单数据
  const [policyData, setPolicyData] = useState({
    id: id,
    policyNumber: 'POL-2024-001',
    type: 'Commercial Property Insurance',
    client: 'ABC Manufacturing Co.',
    status: 'Under Review',
    documents: [
      {
        id: 1,
        name: 'Property Valuation Report.pdf',
        uploadedAt: '2024-01-24',
        status: 'Pending Review',
        remarks: '',
        previewUrl: '/path/to/document.pdf'
      },
      {
        id: 2,
        name: 'Risk Assessment.pdf',
        uploadedAt: '2024-01-25',
        status: 'Pending Review',
        remarks: 'Requires updated fire safety certification',
        previewUrl: '/path/to/document.pdf'
      }
    ],
    reviewHistory: [
      {
        note: 'Initial review completed, requesting additional fire safety documentation',
        date: '2024-01-25',
        author: 'Sarah Wilson',
        type: 'review'
      },
      {
        note: 'Property valuation appears to be outdated, please provide current assessment',
        date: '2024-01-24',
        author: 'Sarah Wilson',
        type: 'review'
      }
    ]
  });

  const handleDocumentClick = (doc) => {
    // 在新窗口打开文档预览页面
    const previewUrl = `/underwriter/document-preview/${doc.id}`;
    const documentState = { document: doc };
    
    // 将 state 数据编码并作为 URL 参数传递
    const stateParam = encodeURIComponent(JSON.stringify(documentState));
    const fullUrl = `${previewUrl}?state=${stateParam}`;
    
    // 使用 window.open 在新窗口打开
    window.open(fullUrl, '_blank');
  };

  const handleDownload = (doc) => {
    console.log('Downloading:', doc.name);
  };

  const handleSubmitReview = () => {
    if (!reviewComment.trim()) return;

    const newNote = {
      note: reviewComment,
      date: new Date().toISOString(),
      author: 'Sarah Wilson',
      type: 'review'
    };

    setPolicyData(prev => ({
      ...prev,
      reviewHistory: [newNote, ...prev.reviewHistory]
    }));

    // 新增提交后的处理逻辑
    setReviewComment('');
    setOpenCompleteDialog(true); // 打开完成审核对话框
    console.log('Review submitted with final note:', reviewComment);
    
    // 如果需要直接完成审核，可以调用：
    // handleCompleteReview();
  };

  // 修改状态处理函数，添加文件名信息
  const handleUpdateDocumentStatus = (docId, newStatus, note) => {
    setPolicyData(prev => {
      const updatedDoc = prev.documents.find(doc => doc.id === docId);
      return {
        ...prev,
        documents: prev.documents.map(doc => {
          if (doc.id === docId) {
            return {
              ...doc,
              status: newStatus,
              remarks: note || doc.remarks,
              updatedAt: new Date().toISOString()
            };
          }
          return doc;
        }),
        reviewHistory: [
          {
            note: `Document "${updatedDoc.name}" status updated to ${newStatus}${note ? `\nNote: ${note}` : ''}`,
            date: new Date().toISOString(),
            author: 'Sarah Wilson',
            type: 'document_review'
          },
          ...prev.reviewHistory
        ]
      };
    });
  };

  const handleCompleteReview = () => {
    // 处理完成审核的逻辑
    console.log('Review completed');
    navigate('/underwriter/policy-review');
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/underwriter/policy-review')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Review Queue
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Policy Review Details
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Review policy details and make underwriting decisions
        </Typography>
      </Box>

      {/* Main Content - Two Column Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Documents Section */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFileIcon /> Submitted Documents
              </Typography>
              <Chip 
                label={`${policyData.documents.length} Documents`}
                size="small"
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
              />
            </Box>
            
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {policyData.documents.map((doc) => (
                <Paper
                  key={doc.id}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    border: '1px solid #e0e0e0',
                    borderLeft: `4px solid ${
                      doc.status === 'Approved' ? '#4caf50' :
                      doc.status === 'Rejected' ? '#f44336' :
                      '#ff9800'
                    }`,
                    '&:hover': { 
                      bgcolor: '#f8f9fa'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {doc.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Uploaded on {doc.uploadedAt}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        size="small"
                        label={doc.status}
                        sx={{ 
                          height: '24px',
                          bgcolor: '#fff3e0',
                          color: '#ed6c02',
                          '& .MuiChip-label': { 
                            px: 1.5,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }
                        }}
                      />
                      <Tooltip title="Preview">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDocumentClick(doc)}
                          sx={{ color: '#1976d2' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDownload(doc)}
                          sx={{ color: '#1976d2' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  {doc.remarks && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 1.5,
                        p: 1.5,
                        bgcolor: '#fff3e0',
                        color: '#ed6c02',
                        borderRadius: 1,
                        fontSize: '0.875rem'
                      }}
                    >
                      {doc.remarks}
                    </Typography>
                  )}

                  {/* 添加状态更新部分 */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Add note..."
                      value={doc.newNote || ''}
                      onChange={(e) => {
                        setPolicyData(prev => ({
                          ...prev,
                          documents: prev.documents.map(d => 
                            d.id === doc.id ? { ...d, newNote: e.target.value } : d
                          )
                        }));
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleUpdateDocumentStatus(doc.id, 'Approved', doc.newNote)}
                      sx={{ minWidth: 100 }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleUpdateDocumentStatus(doc.id, 'Rejected', doc.newNote)}
                      sx={{ minWidth: 100 }}
                    >
                      Reject
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Review Notes */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon /> Review Notes
              </Typography>
              <Chip 
                label={`${policyData.reviewHistory.length} Notes`}
                size="small"
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
              />
            </Box>

            {/* Notes Timeline */}
            <Box sx={{ flex: 1, overflow: 'auto', mb: 3 }}>
              {policyData.reviewHistory.map((note, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    pl: 4,
                    mb: 3,
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      left: '12px',
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      bgcolor: '#e0e0e0'
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '6px',
                      top: '0',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      bgcolor: note.type === 'document_review' ? '#4caf50' : '#f44336',
                      border: '2px solid #fff',
                      boxShadow: `0 0 0 2px ${note.type === 'document_review' ? '#e8f5e9' : '#ffebee'}`
                    }}
                  />

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: note.type === 'document_review' ? '#e8f5e9' : '#ffebee',
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        label={note.type === 'document_review' ? 'Document Review' : 'Review Note'}
                        size="small"
                        sx={{
                          bgcolor: note.type === 'document_review' ? '#4caf50' : '#f44336',
                          color: '#fff',
                          height: '20px'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {note.author} • {new Date(note.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      {note.note}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>

            {/* Add Review Note Section */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#1976d2' }}>
                Submit Final Review
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter your final review comments..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  disabled={!reviewComment.trim()}
                  onClick={handleSubmitReview}
                  sx={{
                    bgcolor: '#1976d2',
                    '&:hover': {
                      bgcolor: '#1565c0'
                    }
                  }}
                >
                  Submit Review
                </Button>
              </Box>
            </Paper>
          </Paper>
        </Grid>
      </Grid>

      {/* 完成审核对话框 */}
      <CompleteReviewDialog
        open={openCompleteDialog}
        onClose={() => setOpenCompleteDialog(false)}
        onSubmit={handleCompleteReview}
        documents={policyData.documents.map(doc => ({
          ...doc,
          type: doc.name.split('.').pop().toLowerCase(),
          reviewDate: new Date().toLocaleDateString(),
          status: doc.status.toLowerCase()
        }))}
      />
    </Container>
  );
};

export default PolicyReviewDetailPage; 